import { useState } from "react";
import {
  ChevronDown,
  ChevronRight,
  Wind,
  Database,
  FileText,
  Shield,
  Zap,
} from "lucide-react";

const IGGOverview = () => {
  const [openSections, setOpenSections] = useState(false);

  const toggleSection = () => {
    setOpenSections((prev) => !prev);
  };

  const dataSources = [
    {
      collection: "VesselIGGEngineData",
      fields: [
        "vesselId",
        "voyageId",
        "date",
        "CargoTankInerting",
        "PurgeGasFree",
        "BlanketingSafety",
        "iggConsMTHFO",
        "iggConsMTLFO",
        "iggConsMTMGO",
      ],
      purpose: "Inert Gas Generator operational data and fuel consumption",
    },
  ];

  const coreCalculations = [
    {
      id: "cargo-tank-inerting",
      title: "Cargo Tank Inerting Calculation",
      description: "Maintains oxygen-free atmosphere in cargo tanks for safety",
      formula:
        "Inert Gas Flow = Tank Volume × Oxygen Reduction Rate × Safety Factor",
      subFormulas: [
        "Prevents flammable atmospheres in cargo tanks",
        "Typical target: Oxygen < 8% for hydrocarbons",
        "Continuous monitoring and adjustment",
        "Safety critical operation",
      ],
      icon: <Shield className="w-5 h-5" />,
      borderColor: "border-purple-200",
      bgColor: "bg-purple-50",
      textColor: "text-purple-700",
    },
    {
      id: "purge-gas-free",
      title: "Purge Gas Free Operation",
      description:
        "Calculates inert gas for purging tanks to gas-free condition",
      formula: "Purge Gas = Tank Volume × Volume Changes × Gas Density",
      subFormulas: [
        "Prepares tanks for inspection or maintenance",
        "Removes hydrocarbon vapors",
        "Requires multiple volume changes",
        "Critical for hot work permits",
      ],
      icon: <Wind className="w-5 h-5" />,
      borderColor: "border-amber-200",
      bgColor: "bg-amber-50",
      textColor: "text-amber-700",
    },
    {
      id: "blanketing-safety",
      title: "Blanketing Safety Margin",
      description: "Determines inert gas for maintaining protective blanket",
      formula: "Blanketing Gas = Evaporation Rate × Safety Coverage Factor",
      subFormulas: [
        "Maintains positive pressure in cargo tanks",
        "Prevents air ingress during cargo operations",
        "Compensates for temperature changes",
        "Continuous operation during cargo handling",
      ],
      icon: <Zap className="w-5 h-5" />,
      borderColor: "border-red-200",
      bgColor: "bg-red-50",
      textColor: "text-red-700",
    },
  ];

  const iggOperations = [
    {
      operation: "Cargo Tank Inerting",
      field: "CargoTankInerting",
      value: "45 kg/h",
      purpose: "Maintain oxygen-free atmosphere in cargo tanks",
      safety: "Critical for preventing flammable atmospheres",
      regulatory: "SOLAS requirements for tanker operations",
    },
    {
      operation: "Purge Gas Free",
      field: "PurgeGasFree",
      value: "30 kg/h",
      purpose: "Prepare tanks for inspection or maintenance",
      safety: "Essential for hot work and entry permits",
      regulatory: "ISGOTT guidelines for tank cleaning",
    },
    {
      operation: "Blanketing Safety",
      field: "BlanketingSafety",
      value: "25 kg/h",
      purpose: "Maintain protective gas blanket during operations",
      safety: "Prevents air ingress and maintains pressure",
      regulatory: "Industry best practices for cargo handling",
    },
  ];

  const safetyParameters = [
    {
      parameter: "Oxygen Level",
      requirement: "< 8%",
      purpose: "Below flammable limit for hydrocarbons",
      monitoring: "Continuous oxygen analyzers",
      action: "Increase inert gas flow if above threshold",
    },
    {
      parameter: "Gas Pressure",
      requirement: "5-10 mbar positive",
      purpose: "Maintain positive tank pressure",
      monitoring: "Pressure transmitters",
      action: "Adjust blower speed as needed",
    },
    {
      parameter: "Gas Quality",
      requirement: "< 5% O₂, low dew point",
      purpose: "Ensure effective inerting",
      monitoring: "Gas analyzers and moisture sensors",
      action: "Adjust combustion parameters",
    },
    {
      parameter: "Flow Rate",
      requirement: "Match cargo operations",
      purpose: "Adequate supply for all operations",
      monitoring: "Flow meters",
      action: "Scale up/down with operational needs",
    },
  ];

  return (
    <div>
      <div className="mb-6 border border-gray-300 rounded-lg overflow-hidden">
        <button
          className="flex items-center justify-between w-full p-5 bg-gray-50 hover:bg-gray-100 transition-colors"
          onClick={toggleSection}
        >
          <div className="flex items-center gap-4">
            <div className="p-3 bg-[#fad8d4] rounded-lg">
              <Wind className="w-6 h-6 text-[#a93226]" />
            </div>
            <div className="text-left">
              <h2 className="text-md font-bold text-gray-800">
                IGG Safety System Calculations
              </h2>
              <p className="text-gray-600 text-sm">
                Inert Gas Generator safety analysis, gas consumption metrics,
                and operational compliance
              </p>
            </div>
          </div>
          {openSections ? (
            <ChevronDown className="w-5 h-5 text-gray-500" />
          ) : (
            <ChevronRight className="w-5 h-5 text-gray-500" />
          )}
        </button>

        {openSections && (
          <div className="p-5 bg-white border-t border-gray-200 space-y-8">
            {/* Data Sources */}
            <div>
              <div className="flex items-center gap-2 mb-4">
                <Database className="w-5 h-5 text-gray-600" />
                <h3 className="text-lg font-semibold text-gray-800">
                  Data Sources & Collections
                </h3>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-1 gap-4">
                {dataSources.map((source, index) => (
                  <div
                    key={index}
                    className="bg-gray-50 p-4 rounded-lg border border-gray-200"
                  >
                    <div className="flex items-center gap-2 mb-2">
                      <FileText className="w-4 h-4 text-gray-600" />
                      <h4 className="font-semibold text-gray-800">
                        {source.collection}
                      </h4>
                    </div>
                    <p className="text-sm text-gray-700 mb-3">
                      {source.purpose}
                    </p>
                    <div className="text-xs text-gray-600 space-y-1">
                      <div className="font-medium mb-1">Key Fields:</div>
                      {source.fields.map((field, idx) => (
                        <div key={idx} className="font-mono truncate">
                          • {field}
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Core Calculations */}
            <div>
              <h3 className="text-lg font-semibold text-gray-800 mb-4">
                Core Calculations & Safety Logic
              </h3>
              <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6">
                {coreCalculations.map((calc) => (
                  <div
                    key={calc.id}
                    className={`p-5 rounded-lg border ${calc.borderColor} ${calc.bgColor}`}
                  >
                    <div className="flex items-start gap-3 mb-4">
                      <div
                        className={`p-2 rounded-lg ${calc.textColor} bg-white`}
                      >
                        {calc.icon}
                      </div>
                      <div className="flex-1">
                        <h4
                          className={`font-bold text-gray-800 mb-1 ${calc.textColor}`}
                        >
                          {calc.title}
                        </h4>
                        <p className="text-sm text-gray-600">
                          {calc.description}
                        </p>
                      </div>
                    </div>

                    <div className="space-y-3">
                      <div>
                        <p className="text-sm font-medium text-gray-700 mb-1">
                          Formula/Logic:
                        </p>
                        <div className="text-sm bg-white p-3 rounded border border-gray-200 font-mono">
                          {calc.formula}
                        </div>
                      </div>

                      {calc.subFormulas && (
                        <div>
                          <p className="text-sm font-medium text-gray-700 mb-2">
                            Safety Considerations:
                          </p>
                          <div className="space-y-2">
                            {calc.subFormulas.map((subFormula, idx) => (
                              <div
                                key={idx}
                                className="text-sm bg-white p-2 rounded border border-gray-100"
                              >
                                {subFormula}
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* IGG Operations */}
            <div>
              <h3 className="text-lg font-semibold text-gray-800 mb-4">
                IGG Safety Operations
              </h3>
              <div className="bg-red-50 p-4 rounded-lg border border-red-200">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {iggOperations.map((op, index) => (
                    <div key={index} className="bg-white p-4 rounded border">
                      <div className="font-semibold text-gray-800 mb-2">
                        {op.operation}
                      </div>
                      <div className="text-xs text-gray-600 mb-1">
                        Database Field:{" "}
                        <span className="font-mono">{op.field}</span>
                      </div>
                      <div className="text-lg font-bold text-red-700 mb-2">
                        Value: {op.value}
                      </div>
                      <p className="text-sm text-gray-700 mb-2">{op.purpose}</p>
                      <div className="text-xs text-red-600 mb-1">
                        Safety: {op.safety}
                      </div>
                      <div className="text-xs text-red-600">
                        Regulatory: {op.regulatory}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default IGGOverview;
